import { useCallback, useEffect, useState, useMemo } from 'react'
import { Navigate } from 'react-router-dom'
import { PageTitle } from '../../_metronic/layout/core'
import { APIError, useAuth } from '../../app/modules/auth'
import axios from 'axios'
import { BASE_API_URL } from '../core/_requests'
import { DateTime } from 'luxon'

const API_URL = `${BASE_API_URL}/admin/api/v1`

// Allowed emails for billing access
const BILLING_ALLOWED_EMAILS: string[] = ['chris@cohoat.tech', 'jnalls@mogulclients.com']

interface LeadBreakdown {
  divine_memories: number
  divine_memories_events: number
  divine_memories_checkins: number
  direct_mail: number
  ngl_portal: number
}

interface PremierLocation {
  id: string
  name: string
  unique_identifier: string
  last_activity: string | null
  leads_count: number
  lead_breakdown: LeadBreakdown
}

interface PassareOrganization {
  id: string
  name: string
  unique_identifier: string
  last_activity: string | null
  leads_count: number
}

interface FCAOrganization {
  org_id: string
  name: string
  last_activity: string | null
  leads_count: number
}

interface WebhookAccount {
  id: string
  name: string
  unique_identifier: string
  last_activity: string | null
  leads_count: number
}

interface BillingSummary {
  total_active_funeral_homes: number
  premier_service_locations: number
  passare_organizations: number
  full_circle_organizations: number
  webhook_accounts: number
  total_leads: number
  premier_leads: number
  passare_leads: number
  fca_leads: number
  webhook_leads: number
}

interface BillingData {
  period: string
  start_date: string
  end_date: string
  summary: BillingSummary
  premier_service_locations: PremierLocation[]
  passare_organizations: PassareOrganization[]
  full_circle_organizations: FCAOrganization[]
  webhook_accounts: WebhookAccount[]
}

// Unified row for the combined table
interface UnifiedFuneralHome {
  id: string
  name: string
  divine: number
  directMail: number
  portal: number
  passare: number
  fca: number
  webhook: number
  totalLeads: number
  lastActivity: string | null
}

type SortField = 'name' | 'total' | 'divine' | 'directMail' | 'portal' | 'passare' | 'fca' | 'webhook'
type SortDirection = 'asc' | 'desc'

function generateCSV(data: UnifiedFuneralHome[]): string {
  const headers = ['Account', 'Total', 'Divine', 'Direct Mail', 'Portal', 'Passare', 'FCA', 'Webhook', 'Last Activity']
  const rows = data.map(row => [
    `"${row.name.replace(/"/g, '""')}"`,
    row.totalLeads.toString(),
    row.divine.toString(),
    row.directMail.toString(),
    row.portal.toString(),
    row.passare.toString(),
    row.fca.toString(),
    row.webhook.toString(),
    row.lastActivity || ''
  ])
  return [headers.join(','), ...rows.map(r => r.join(','))].join('\n')
}

function downloadCSV(content: string, filename: string) {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  link.href = URL.createObjectURL(blob)
  link.download = filename
  link.click()
  URL.revokeObjectURL(link.href)
}

interface DateRangeParams {
  month?: string
  startDate?: string
  endDate?: string
}

async function getActiveFuneralHomes(params: DateRangeParams): Promise<BillingData> {
  const queryParams = new URLSearchParams()
  if (params.startDate && params.endDate) {
    queryParams.set('start_date', params.startDate)
    queryParams.set('end_date', params.endDate)
  } else if (params.month) {
    queryParams.set('month', params.month)
  }
  const queryString = queryParams.toString()
  const url = `${API_URL}/billing/active-funeral-homes${queryString ? `?${queryString}` : ''}`
  const response = await axios.get(url)
  return response.data.data as BillingData
}

function getAvailableMonths(): { value: string; label: string }[] {
  const months: { value: string; label: string }[] = []
  const now = DateTime.now()

  for (let i = 0; i < 12; i++) {
    const date = now.minus({ months: i })
    months.push({
      value: date.toFormat('yyyy-MM'),
      label: date.toFormat('MMMM yyyy')
    })
  }

  return months
}

function formatDateHuman(dateStr: string): string {
  return DateTime.fromISO(dateStr).toFormat('MMMM d, yyyy')
}

function buildUnifiedData(data: BillingData): UnifiedFuneralHome[] {
  const unified: UnifiedFuneralHome[] = []

  // Add Premier locations (Divine, Direct Mail, Portal)
  for (const loc of data.premier_service_locations) {
    unified.push({
      id: `premier-${loc.id}`,
      name: loc.name,
      divine: loc.lead_breakdown.divine_memories,
      directMail: loc.lead_breakdown.direct_mail,
      portal: loc.lead_breakdown.ngl_portal,
      passare: 0,
      fca: 0,
      webhook: 0,
      totalLeads: loc.leads_count,
      lastActivity: loc.last_activity
    })
  }

  // Add Passare organizations
  for (const org of data.passare_organizations) {
    unified.push({
      id: `passare-${org.id}`,
      name: org.name,
      divine: 0,
      directMail: 0,
      portal: 0,
      passare: org.leads_count,
      fca: 0,
      webhook: 0,
      totalLeads: org.leads_count,
      lastActivity: org.last_activity
    })
  }

  // Add FCA organizations
  for (const org of data.full_circle_organizations) {
    unified.push({
      id: `fca-${org.org_id}`,
      name: org.name,
      divine: 0,
      directMail: 0,
      portal: 0,
      passare: 0,
      fca: org.leads_count,
      webhook: 0,
      totalLeads: org.leads_count,
      lastActivity: org.last_activity
    })
  }

  // Add Webhook accounts
  for (const acc of data.webhook_accounts) {
    unified.push({
      id: `webhook-${acc.id}`,
      name: acc.name,
      divine: 0,
      directMail: 0,
      portal: 0,
      passare: 0,
      fca: 0,
      webhook: acc.leads_count,
      totalLeads: acc.leads_count,
      lastActivity: acc.last_activity
    })
  }

  return unified
}


function SortableHeader({
  label,
  field,
  currentSort,
  currentDirection,
  onSort,
  className = ''
}: {
  label: string
  field: SortField
  currentSort: SortField
  currentDirection: SortDirection
  onSort: (field: SortField) => void
  className?: string
}) {
  const isActive = currentSort === field
  return (
    <th
      className={`${className} cursor-pointer user-select-none`}
      onClick={() => onSort(field)}
      style={{ cursor: 'pointer' }}
    >
      {label}
      {isActive && (
        <span className="ms-1">
          {currentDirection === 'asc' ? '↑' : '↓'}
        </span>
      )}
    </th>
  )
}

function LeadCell({ value }: { value: number }) {
  if (value === 0) {
    return <td className="text-center text-gray-300">-</td>
  }
  return (
    <td className="text-center text-gray-700">{value}</td>
  )
}

function UnifiedTable({
  data,
  includedIds,
  onToggle
}: {
  data: UnifiedFuneralHome[]
  includedIds: Set<string>
  onToggle: (id: string) => void
}) {
  const [sortField, setSortField] = useState<SortField>('name')
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc')

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection(field === 'name' ? 'asc' : 'desc')
    }
  }

  const sortedData = useMemo(() => {
    return [...data].sort((a, b) => {
      const multiplier = sortDirection === 'asc' ? 1 : -1
      switch (sortField) {
        case 'name':
          return multiplier * a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
        case 'total':
          return multiplier * (a.totalLeads - b.totalLeads)
        case 'divine':
          return multiplier * (a.divine - b.divine)
        case 'directMail':
          return multiplier * (a.directMail - b.directMail)
        case 'portal':
          return multiplier * (a.portal - b.portal)
        case 'passare':
          return multiplier * (a.passare - b.passare)
        case 'fca':
          return multiplier * (a.fca - b.fca)
        case 'webhook':
          return multiplier * (a.webhook - b.webhook)
        default:
          return 0
      }
    })
  }, [data, sortField, sortDirection])

  if (data.length === 0) {
    return (
      <div className="text-center py-10 text-gray-500">
        No active funeral homes found for this period.
      </div>
    )
  }

  return (
    <div className="table-responsive">
      <table className="table table-row-bordered table-row-gray-200 align-middle gs-0 gy-3">
        <thead>
          <tr className="fw-bold text-gray-500 bg-light">
            <th className="ps-4 w-40px rounded-start"></th>
            <SortableHeader
              label="Account"
              field="name"
              currentSort={sortField}
              currentDirection={sortDirection}
              onSort={handleSort}
              className="min-w-200px"
            />
            <SortableHeader
              label="Total"
              field="total"
              currentSort={sortField}
              currentDirection={sortDirection}
              onSort={handleSort}
              className="text-center"
            />
            <SortableHeader
              label="Divine"
              field="divine"
              currentSort={sortField}
              currentDirection={sortDirection}
              onSort={handleSort}
              className="text-center"
            />
            <SortableHeader
              label="Direct Mail"
              field="directMail"
              currentSort={sortField}
              currentDirection={sortDirection}
              onSort={handleSort}
              className="text-center"
            />
            <SortableHeader
              label="Portal"
              field="portal"
              currentSort={sortField}
              currentDirection={sortDirection}
              onSort={handleSort}
              className="text-center"
            />
            <SortableHeader
              label="Passare"
              field="passare"
              currentSort={sortField}
              currentDirection={sortDirection}
              onSort={handleSort}
              className="text-center"
            />
            <SortableHeader
              label="FCA"
              field="fca"
              currentSort={sortField}
              currentDirection={sortDirection}
              onSort={handleSort}
              className="text-center"
            />
            <SortableHeader
              label="Webhook"
              field="webhook"
              currentSort={sortField}
              currentDirection={sortDirection}
              onSort={handleSort}
              className="text-center"
            />
            <th className="pe-4 rounded-end">Last Activity</th>
          </tr>
        </thead>
        <tbody>
          {sortedData.map((row) => {
            const isIncluded = includedIds.has(row.id)
            return (
              <tr key={row.id} className={isIncluded ? '' : 'opacity-50'}>
                <td className="ps-4">
                  <div className="form-check form-check-sm form-check-custom form-check-solid">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={isIncluded}
                      onChange={() => onToggle(row.id)}
                    />
                  </div>
                </td>
                <td>
                  <div className="fw-bold text-gray-800">{row.name}</div>
                </td>
                <td className="text-center">
                  <span className="badge badge-light-primary fs-7 fw-bold">{row.totalLeads}</span>
                </td>
                <LeadCell value={row.divine} />
                <LeadCell value={row.directMail} />
                <LeadCell value={row.portal} />
                <LeadCell value={row.passare} />
                <LeadCell value={row.fca} />
                <LeadCell value={row.webhook} />
                <td className="pe-4 text-gray-500 fs-7">
                  {row.lastActivity
                    ? DateTime.fromISO(row.lastActivity).toRelative()
                    : 'N/A'}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function BillingDashboardContent() {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [data, setData] = useState<BillingData | null>(null)
  const [dateMode, setDateMode] = useState<'month' | 'custom'>('month')
  const [selectedMonth, setSelectedMonth] = useState<string>('')
  const [customStartDate, setCustomStartDate] = useState<string>('')
  const [customEndDate, setCustomEndDate] = useState<string>('')
  const [includedIds, setIncludedIds] = useState<Set<string>>(new Set())

  const availableMonths = useMemo(() => getAvailableMonths(), [])

  const loadData = useCallback(async (params: DateRangeParams) => {
    setLoading(true)
    setError(null)
    try {
      const billingData = await getActiveFuneralHomes(params)
      setData(billingData)
    } catch (exception) {
      console.error(exception)
      const errorMessage = exception instanceof APIError
        ? exception.message
        : 'Failed to load billing data'
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (dateMode === 'custom' && customStartDate && customEndDate) {
      loadData({ startDate: customStartDate, endDate: customEndDate })
    } else if (dateMode === 'month') {
      loadData({ month: selectedMonth || undefined })
    }
  }, [loadData, dateMode, selectedMonth, customStartDate, customEndDate])

  const unifiedData = useMemo(() => {
    if (!data) return []
    return buildUnifiedData(data)
  }, [data])

  // Initialize includedIds when data changes (all checked by default)
  useEffect(() => {
    if (unifiedData.length > 0) {
      setIncludedIds(new Set(unifiedData.map(row => row.id)))
    }
  }, [unifiedData])

  const handleToggle = useCallback((id: string) => {
    setIncludedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }, [])

  // Filter data to only included rows for summary/export
  const includedData = useMemo(() => {
    return unifiedData.filter(row => includedIds.has(row.id))
  }, [unifiedData, includedIds])

  const handleExport = useCallback(() => {
    if (!data || includedData.length === 0) return
    const csv = generateCSV(includedData)
    const filename = `billing-${data.period.replace(/\s+/g, '-').toLowerCase()}.csv`
    downloadCSV(csv, filename)
  }, [data, includedData])

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center py-20">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="alert alert-danger">
        <strong>Error:</strong> {error}
      </div>
    )
  }

  if (!data) {
    return null
  }

  return (
    <>
      {/* Header with Date Range */}
      <div className="card mb-8">
        <div className="card-body py-6">
          <div className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-4">
            {/* Period Display */}
            <div>
              <h2 className="fw-bolder text-gray-900 mb-2 fs-1">{data.period}</h2>
              <div className="d-flex align-items-center gap-2">
                <span className="badge badge-light-primary fs-6 fw-bold px-4 py-2">
                  {formatDateHuman(data.start_date)}
                </span>
                <span className="text-gray-500 fw-bold">to</span>
                <span className="badge badge-light-primary fs-6 fw-bold px-4 py-2">
                  {formatDateHuman(data.end_date)}
                </span>
              </div>
            </div>

            {/* Date Selection */}
            <div className="d-flex flex-column flex-sm-row align-items-sm-center gap-3">
              <div className="btn-group" role="group">
                <button
                  type="button"
                  className={`btn btn-sm ${dateMode === 'month' ? 'btn-primary' : 'btn-light'}`}
                  onClick={() => setDateMode('month')}
                >
                  Month
                </button>
                <button
                  type="button"
                  className={`btn btn-sm ${dateMode === 'custom' ? 'btn-primary' : 'btn-light'}`}
                  onClick={() => setDateMode('custom')}
                >
                  Custom Range
                </button>
              </div>

              {dateMode === 'month' ? (
                <select
                  className="form-select form-select-sm"
                  style={{ width: '180px' }}
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                >
                  <option value="">Previous Month</option>
                  {availableMonths.map((month) => (
                    <option key={month.value} value={month.value}>
                      {month.label}
                    </option>
                  ))}
                </select>
              ) : (
                <div className="d-flex align-items-center gap-2">
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                  />
                  <span className="text-gray-500">to</span>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Summary - calculated from included data only */}
      <div className="d-flex align-items-center gap-4 mb-8">
        <div className="d-flex align-items-baseline gap-2">
          <span className="fs-2x fw-bolder text-primary">{includedData.length}</span>
          <span className="text-gray-600 fw-semibold">active accounts</span>
        </div>
        <div className="text-gray-400">|</div>
        <div className="d-flex align-items-baseline gap-2">
          <span className="fs-2x fw-bolder text-success">
            {includedData.reduce((sum, row) => sum + row.totalLeads, 0).toLocaleString()}
          </span>
          <span className="text-gray-600 fw-semibold">total leads</span>
        </div>
        {includedData.length < unifiedData.length && (
          <>
            <div className="text-gray-400">|</div>
            <span className="text-gray-500 fs-7">
              {unifiedData.length - includedData.length} excluded
            </span>
          </>
        )}
      </div>

      {/* Unified Table */}
      <div className="card">
        <div className="card-header border-0 pt-5">
          <h3 className="card-title align-items-start flex-column">
            <span className="card-label fw-bold text-gray-800">
              All Active Funeral Homes
              <span className="badge badge-light-primary ms-2">{unifiedData.length}</span>
            </span>
            <span className="text-gray-400 mt-1 fw-semibold fs-7">
              Uncheck accounts to exclude from summary and export
            </span>
          </h3>
          <div className="card-toolbar">
            <button
              type="button"
              className="btn btn-sm btn-light-primary"
              onClick={handleExport}
              disabled={includedData.length === 0}
            >
              <i className="bi bi-download fs-4 me-2"></i>
              Export CSV
            </button>
          </div>
        </div>
        <div className="card-body py-3">
          <UnifiedTable
            data={unifiedData}
            includedIds={includedIds}
            onToggle={handleToggle}
          />
        </div>
      </div>
    </>
  )
}

const BillingDashboardPage = () => {
  const { currentUser } = useAuth()

  // Check if user is allowed to access billing
  if (!currentUser || !BILLING_ALLOWED_EMAILS.includes(currentUser.email)) {
    return <Navigate to="/dashboard" />
  }

  return (
    <>
      <PageTitle breadcrumbs={[
        { title: 'Utilities', path: '/', isSeparator: false, isActive: false },
        { title: '', path: '', isSeparator: true, isActive: false }
      ]}>
        Billing Dashboard
      </PageTitle>
      <div className="mt-6">
        <BillingDashboardContent />
      </div>
    </>
  )
}

export default BillingDashboardPage
